import { useMemo } from 'react';

import {
  useGetFreshStrawAccountByNumberQuery,
  useGetFreshStrawSiteByCodeQuery,
} from '@/app/services/freshStrawRest';
import {
  type SchedulerTripDetail,
  type SchedulerTripStop,
  useGetSchedulerTripByIdQuery,
} from '@/app/services/serviceScheduler';

/**
 * Fetches the service-scheduler trip for a trip-scoped fresh-straw session
 * and resolves the matching stop. Returns a flattened `TripContext` the
 * banner + filter panel can read.
 *
 * Site/account auto-resolution: the scheduler stop carries a free-form
 * `siteCode` string and an `accountNumber` string. We call the
 * `fresh-straw/lookups/site-by-code` and `account-by-number` endpoints to
 * turn those into Guids; when a match is found, `resolvedSiteId` /
 * `resolvedAccountId` are populated and the hint fields are kept around for
 * display. If no match, the banner shows the hint and the user picks
 * manually in the filter panel.
 */

export interface FreshStrawTripContext {
  isLoading: boolean;
  error: unknown;
  /** Raw trip detail (undefined while loading or missing). */
  trip: SchedulerTripDetail | undefined;
  /** The specific stop that matches the stopId URL param. */
  stop: SchedulerTripStop | undefined;
  /** Shipment number (legacy "trip number") for display. */
  shipmentNumber: string | undefined;
  /** Stop start date, ISO `yyyy-mm-dd`, for auto-population into transferDate. */
  transferDateFromStop: string | undefined;
  /** Free-form display value for the site. Not a Guid — do not send to save endpoints. */
  siteCodeHint: string | undefined;
  /** Free-form display value for the account. Not a Guid — do not send to save endpoints. */
  accountNumberHint: string | undefined;
  /** Guid resolved from `siteCodeHint` via the site-by-code lookup. Undefined until the lookup completes or when no match is found. */
  resolvedSiteId: string | undefined;
  /** Guid resolved from `accountNumberHint` via the account-by-number lookup. Undefined until the lookup completes or when no match is found. */
  resolvedAccountId: string | undefined;
  /** Vet equipment id from the stop, if present. */
  psVetId: string | undefined;
  /** Embryologist equipment id from the trip header, if present. */
  psEmbryologistId: string | undefined;
  /** Comments on the stop — display only. */
  comments: string | undefined;
}

const EMPTY: FreshStrawTripContext = {
  isLoading: false,
  error: undefined,
  trip: undefined,
  stop: undefined,
  shipmentNumber: undefined,
  transferDateFromStop: undefined,
  siteCodeHint: undefined,
  accountNumberHint: undefined,
  resolvedSiteId: undefined,
  resolvedAccountId: undefined,
  psVetId: undefined,
  psEmbryologistId: undefined,
  comments: undefined,
};

function toDateOnly(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const split = iso.split('T')[0];
  return split && /^\d{4}-\d{2}-\d{2}$/.test(split) ? split : undefined;
}

export function useFreshStrawTripContext(
  tripId: string,
  stopId: string,
): FreshStrawTripContext {
  const skip = !tripId;
  const {
    data: trip,
    isLoading,
    error,
  } = useGetSchedulerTripByIdQuery(tripId, { skip });

  const stop = trip?.stops?.find((s) => s.stopId === stopId);
  const siteCodeHint = stop?.siteCode || undefined;
  const accountNumberHint = stop?.accountNumber || undefined;

  const { data: siteLookup } = useGetFreshStrawSiteByCodeQuery(
    siteCodeHint ?? '',
    { skip: !siteCodeHint },
  );
  const { data: accountLookup } = useGetFreshStrawAccountByNumberQuery(
    accountNumberHint ?? '',
    { skip: !accountNumberHint },
  );

  return useMemo<FreshStrawTripContext>(() => {
    if (skip) return EMPTY;
    if (!trip) {
      return { ...EMPTY, isLoading, error };
    }

    return {
      isLoading,
      error,
      trip,
      stop,
      shipmentNumber: trip.shipmentNumber || undefined,
      transferDateFromStop: toDateOnly(stop?.startUtc),
      siteCodeHint,
      accountNumberHint,
      resolvedSiteId: siteLookup?.id,
      resolvedAccountId: accountLookup?.id,
      psVetId: stop?.psVetId || undefined,
      psEmbryologistId: trip.psEmbryologistId || undefined,
      comments: stop?.comments || undefined,
    };
  }, [
    trip,
    stop,
    siteCodeHint,
    accountNumberHint,
    siteLookup,
    accountLookup,
    isLoading,
    error,
    skip,
  ]);
}
